// apiService.js
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import config from 'config';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

// Hook pour GET
export const useFetchData = (url) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const toast = useToast();
  const { t } = useTranslation();
  const isMountedRef = useRef(true);
  const latestRequestRef = useRef(0);

  // Function to trigger a refetch
  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    const cancelTokenSource = axios.CancelToken.source();
    // Track the current request to avoid stale state updates from old requests
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;

    const fetchData = async () => {
      try {
        if (!url) {
          // no URL
          return;
        }

        // Skip API calls on public pages (auth layout)
        if (window.location.hash.startsWith('#/auth')) {
          return;
        }

        if (Cookies.get('loggedIn')) {
          // Build URL with profilId if available
          let fullUrl = `${config.apiUrl}/${url}`;
          const currentProfileId = Cookies.get('currentProfileId');
          if (currentProfileId) {
            const separator = url.includes('?') ? '&' : '?';
            fullUrl = `${fullUrl}${separator}profilId=${currentProfileId}`;
          }

          // Fetch data (HttpOnly cookie sent automatically via withCredentials)
          const response = await axios.get(fullUrl, {
            cancelToken: cancelTokenSource.token
          });

          if (!isMountedRef.current || requestId !== latestRequestRef.current) return;

          setData(response.data);
        }

      } catch (err) {
        // Ignore cancelled requests
        if (axios.isCancel(err)) {
          return;
        }
        
        if (!isMountedRef.current || requestId !== latestRequestRef.current) return;
        
        setError(err);
        if (err.response && err.response.status === 403) {
          // Forbidden
        } else if (err.response && err.response.status === 423) {
          toast({
            title: t("common.error"),
            description: t("errors.account_disabled"),
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
          Cookies.remove('loggedIn');
          Cookies.remove('token');
          Cookies.remove('currentProfileId');
          window.location.reload();
        } else if (err.response) {
          toast({
            title: t("common.error"),
            description: t("errors.load_data"),
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        }
        // Si pas de response (ex: backend non démarré), on ne fait rien
      } finally {
        if (isMountedRef.current && requestId === latestRequestRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      cancelTokenSource.cancel('Component unmounted');
    };
  }, [url, t, toast, refetchTrigger]);

  return { data, isLoading, error, refetch };
};

// Hook pour POST
export const usePostData = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();
  const { t } = useTranslation();

  const postData = async (url, data, options = {}) => {
    const { suppressErrorToast = false, timeout } = options;
    setIsLoading(true);
    setError(null);

    try {
      const axiosConfig = {};
      if (typeof timeout === 'number' && timeout > 0) {
        axiosConfig.timeout = timeout;
      }
      const response = await axios.post(`${config.apiUrl}/${url}`, data, axiosConfig);

      if (response.status !== 200 && !suppressErrorToast) {
        toast({
          title: t("common.error"),
          description: t("errors.send_data"),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }

      return response.data;
    } catch (err) {
      setError(err);
      if (!suppressErrorToast) {
        if (err.response && err.response.status === 403) {
          toast({
            title: t("common.oops"),
            description: t("errors.forbidden"),
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        } else if (err.response && err.response.status === 401) {
          // Don't show toast for 401 - let the calling code handle it with specific message
          // This prevents double toasts when the caller also shows a specific error message
        } else {
          toast({
            title: t("common.error"),
            description: t("errors.send_data"),
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        }
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { postData, isLoading, error };
};

// Hook pour PUT
export const usePutData = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();
  const { t } = useTranslation();

  const putData = async (url, data) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.put(`${config.apiUrl}/${url}`, data, {});

      if (response.status !== 200) {
        toast({
          title: t("common.error"),
          description: t("errors.update_data"),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }

      return response.data;
    } catch (err) {
      setError(err);
      if (err.response && err.response.status === 403) {
        toast({
          title: t("common.oops"),
          description: t("errors.forbidden"),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } else if (err.response && err.response.status === 401) {
        throw err;
      } else {
        toast({
          title: t("common.error"),
          description: t("errors.update_data"),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { putData, isLoading, error };
};
